import { IssueDetail } from "models/issue";
import { OnlyAction, OnlyIssue } from "models/resolutions";
import { memo, useCallback } from "react";
import IndividualIssueActionTable from "features/Issues/Table/IndividualIssueActionTable";
import { timingIssueAllActionsSentOrSkipped } from "features/Issues/helpers";
import { invalidateIssueQueries, updateIssueActionList, useActionIssuesResolvedQuery, useIssueDetailQuery } from "features/Issues/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { Box } from "@chakra-ui/react";
import SentStatus from "components/Status/SentStatus";
import SkippedStatus from "components/Status/SkippedStatus";
import { PrimarySendNowButton } from "features/Issues/Components/Button/SendNowButton";
import { GraySkipButton } from "features/Issues/Components/Button/SkipButton";
import { ActionV2 } from "features/Resources/Shared/models";
import { isActionTimingValid, actionStatusInfo } from "features/Resources/Shared/helpers";
import { useActionDetailQueryByPrefixId } from "features/Resources/Shared/hooks";

const MemoizedIndividualIssueActionTable = memo(IndividualIssueActionTable);

export function useTimingResolutionValue({ issue }: OnlyIssue) {
  const { data: issueDetail } = useIssueDetailQuery(issue?.id);
  return timingIssueAllActionsSentOrSkipped(issueDetail);
}

export function useTimingActionValue({ action }: OnlyAction) {
  const { data: updatedAction } = useActionDetailQueryByPrefixId(action?.prefixId, { enabled: !!action?.prefixId });
  return isActionTimingValid(updatedAction as ActionV2);
}

export function TimingIssueModalInputFields({ issue }: { issue: IssueDetail }) {
  return <MemoizedIndividualIssueActionTable issueId={issue?.id} />;
}

export function IndividualActionTimingIssueModalInputFields({ issue, action }: { issue: IssueDetail, action: ActionV2 }) {
  const { data } = useActionIssuesResolvedQuery(action?.prefixId);
  const queryClient = useQueryClient();

  const onSuccess = useCallback((resp: ActionV2) => {
    if (issue?.id) {
      updateIssueActionList({ queryClient, issueId: issue?.id, action: resp });
      invalidateIssueQueries(queryClient, issue);
    }
  // only need to change this if the issue is a different issue, which it should never be
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.id, queryClient]);

  if (!action) {
    return null;
  }
  const { isDone } = actionStatusInfo(action);
  const isReadyToSend = data?.nonTiming?.resolved ?? false;

  return action?.status === "skipped" ? (
    <Box display="flex" alignItems="center" gap="2">
      <SkippedStatus action={action} />
      <GraySkipButton onSuccess={onSuccess} action={action} />
    </Box>
  ) : isDone ? (
    <Box display="flex" alignItems="center" gap="2">
      <SentStatus action={action} />
    </Box>
  ) : (
    <Box display="flex" alignItems="center" gap="2">
      <PrimarySendNowButton isDisabled={!isReadyToSend} onSuccess={onSuccess} minWidth="96px" action={action} />
      <GraySkipButton onSuccess={onSuccess} action={action} />
    </Box>
  );
}
